import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import SiteIcon from "./SiteIcon";

function reorder(list, startIndex, endIndex) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
}

const LIST_WRAPPER_CLASS = "px-3 pt-2 pb-6 pr-4 space-y-2";

const CARD_BASE_CLASS =
    "group rounded-xl border border-slate-200 bg-white/80 p-3 cursor-pointer " +
    "transition-all duration-200 ease-out dark:border-slate-700 dark:bg-slate-900/80 " +
    "hover:bg-slate-50 hover:-translate-y-[1px] dark:hover:bg-slate-800/90";

const CARD_ACTIVE_CLASS =
    " shadow-md ring-2 ring-sky-300/70 dark:ring-sky-600/70 scale-[1.01] -translate-y-[1px]";

const CARD_DRAGGING_CLASS = " shadow-lg opacity-95";

const CARD_FOLDER_TARGET_CLASS =
    " bg-amber-50/80 dark:bg-amber-900/20";

const SiteCard = React.memo(function SiteCard({
    site,
    isActive,
    isDragging,
    isCombineTarget,
    onClick,
}) {
    const isFolder = site.type === "folder";

    const secondaryText = site.shortDescription
        ? site.shortDescription
        : isFolder
            ? "Open folder"
            : "No short description";

    return (
        <div
            onClick={onClick}
            className={
                CARD_BASE_CLASS +
                (isActive ? CARD_ACTIVE_CLASS : "") +
                (isDragging ? CARD_DRAGGING_CLASS : "") +
                (isFolder && isCombineTarget ? CARD_FOLDER_TARGET_CLASS : "")
            }
        >
            <div className="grid grid-cols-[48px_1fr] gap-3 items-center">
                <SiteIcon
                    siteUrl={isFolder ? undefined : site.siteUrl}
                    logoUrl={site.logoUrl}
                    name={site.name}
                    size="md"
                    className="rounded-lg"
                    isFolder={isFolder}
                />

                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-900 dark:text-slate-50 truncate">
                            {isFolder ? `📁 ${site.name}` : site.name}
                        </p>

                        <span
                            className={
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium " +
                                (isFolder
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                    : "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300")
                            }
                        >
                            {isFolder ? "Folder" : "Site"}
                        </span>
                    </div>

                    <p
                        className={
                            "mt-1 text-[11px] text-slate-500 dark:text-slate-300 " +
                            (isActive ? "" : "truncate")
                        }
                    >
                        {secondaryText}
                    </p>
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between pl-1 pr-1">
                {isFolder ? (
                    <span className="text-[12px] font-medium text-amber-600 dark:text-amber-400">
                        Drag here to move inside →
                    </span>
                ) : site.siteUrl ? (
                    <a
                        href={site.siteUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[12px] font-medium text-sky-600 hover:text-sky-700 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
                    >
                        Open ↗
                    </a>
                ) : (
                    <span className="text-[12px] text-slate-400 dark:text-slate-500">
                        No URL
                    </span>
                )}

                <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    {site.isPersonal ? "Personal" : "Public"}
                </span>
            </div>
        </div>
    );
});

export default function SiteTable({
    sites,
    selectedSite,
    onSelect,
    isAdmin,
    scope,
    onScopeChange,
    onReorder,
    currentFolderId,
    currentFolder,
    breadcrumbs = [],
    onMoveToFolder,
    onMoveToRoot,
    goToBreadcrumb,
    goToRoot,
}) {
    const [activeBreadcrumbTarget, setActiveBreadcrumbTarget] = useState(null);
    const [isDraggingNow, setIsDraggingNow] = useState(false);

    const visibleSites = useMemo(() => {
        let result = Array.isArray(sites) ? sites : [];

        if (isAdmin && scope) {
            if (scope === "public") {
                result = result.filter((s) => !s.isPersonal);
            } else if (scope === "own") {
                result = result.filter((s) => !!s.isPersonal);
            }
        }

        return result;
    }, [sites, isAdmin, scope]);

    useEffect(() => {
        if (!visibleSites?.length) {
            onSelect(null);
            return;
        }

        // if (!selectedSite || !visibleSites.some(s => s.id === selectedSite.id)) {
        //     console.log(222);
        //     onSelect(visibleSites[0]);
        // }
    }, [selectedSite, visibleSites]);

    const handleRowClick = (site) => {
        onSelect(site);
    };

    const handleBreadcrumbRootClick = () => {
        if (isDraggingNow) return;
        goToRoot && goToRoot();
    };

    const handleBreadcrumbClick = (crumb) => {
        if (isDraggingNow) return;
        goToBreadcrumb && goToBreadcrumb(crumb);
    };

    const findSiteById = (id) => {
        return visibleSites.find((item) => String(item.id) === String(id)) || null;
    };

    const findBreadcrumbTargetFromPoint = (x, y) => {
        if (typeof x !== "number" || typeof y !== "number") return null;

        const elements = document.elementsFromPoint(x, y);
        if (!elements || elements.length === 0) return null;

        for (const el of elements) {
            if (!(el instanceof HTMLElement)) continue;

            const target = el.closest("[data-drop-folder-id]");
            if (!target) continue;

            const raw = target.getAttribute("data-drop-folder-id");
            if (raw === "root") return "root";

            const parsed = Number(raw);
            return Number.isNaN(parsed) ? null : parsed;
        }

        return null;
    };

    useEffect(() => {
        if (!isDraggingNow) {
            setActiveBreadcrumbTarget(null);
            return;
        }

        const handleMouseMove = (e) => {
            const target = findBreadcrumbTargetFromPoint(e.clientX, e.clientY);
            setActiveBreadcrumbTarget(target);
        };

        const clearTarget = () => {
            setActiveBreadcrumbTarget(null);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", clearTarget);
        window.addEventListener("dragend", clearTarget);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", clearTarget);
            window.removeEventListener("dragend", clearTarget);
        };
    }, [isDraggingNow]);

    const handleDragStart = () => {
        setIsDraggingNow(true);
    };

    const handleDragEnd = (result) => {
        const { source, destination, combine, draggableId } = result;

        const sourceItem = findSiteById(draggableId);

        setIsDraggingNow(false);

        if (!sourceItem) {
            setActiveBreadcrumbTarget(null);
            return;
        }

        if (combine && String(combine.droppableId) === "sites-list") {
            const targetItem = findSiteById(combine.draggableId);

            if (
                targetItem &&
                targetItem.type === "folder" &&
                String(targetItem.id) !== String(sourceItem.id)
            ) {
                setActiveBreadcrumbTarget(null);
                onMoveToFolder && onMoveToFolder(sourceItem, targetItem.id);
                return;
            }
        }

        if (activeBreadcrumbTarget === "root") {
            setActiveBreadcrumbTarget(null);
            onMoveToRoot && onMoveToRoot(sourceItem);
            return;
        }

        if (
            activeBreadcrumbTarget !== null &&
            activeBreadcrumbTarget !== "root"
        ) {
            setActiveBreadcrumbTarget(null);
            onMoveToFolder && onMoveToFolder(sourceItem, activeBreadcrumbTarget);
            return;
        }

        if (!destination) {
            setActiveBreadcrumbTarget(null);
            return;
        }

        if (source.index === destination.index) {
            setActiveBreadcrumbTarget(null);
            return;
        }

        if (
            source.droppableId !== "sites-list" ||
            destination.droppableId !== "sites-list"
        ) {
            setActiveBreadcrumbTarget(null);
            return;
        }

        const newVisible = reorder(
            visibleSites,
            source.index,
            destination.index
        );

        setActiveBreadcrumbTarget(null);

        if (onReorder) {
            onReorder(newVisible);
        }
    };

    const title =
        currentFolderId !== null && currentFolderId !== undefined
            ? (currentFolder?.name || "Folder")
            : "Company websites";

    const header = (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200/80 dark:border-slate-700/80 bg-white/50 dark:bg-slate-800/40 backdrop-blur-sm rounded-t-2xl">
            <div>
                <h2 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                    {title}
                </h2>
            </div>

            {isAdmin && (
                <div className="flex items-center gap-2">
                    <div className="flex rounded-full bg-slate-100 p-1 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        <button
                            type="button"
                            onClick={() => onScopeChange("all")}
                            className={
                                "px-2 py-1 rounded-full transition-colors " +
                                (scope === "all"
                                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-50"
                                    : "opacity-70")
                            }
                        >
                            All
                        </button>
                        <button
                            type="button"
                            onClick={() => onScopeChange("public")}
                            className={
                                "px-2 py-1 rounded-full transition-colors " +
                                (scope === "public"
                                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-50"
                                    : "opacity-70")
                            }
                        >
                            General
                        </button>
                        <button
                            type="button"
                            onClick={() => onScopeChange("own")}
                            className={
                                "px-2 py-1 rounded-full transition-colors " +
                                (scope === "own"
                                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-50"
                                    : "opacity-70")
                            }
                        >
                            My
                        </button>
                    </div>

                    {isAdmin && onReorder && scope === "all" && (
                        <span className="hidden md:inline text-[11px] text-slate-400 dark:text-slate-500">
                            Drag to reorder or drop on folder/breadcrumb to move
                        </span>
                    )}
                </div>
            )}
        </div>
    );

    const breadcrumbBar = (
        <div
            className="
                px-3 pt-3 pb-2
                min-h-[52px]
                flex items-center gap-2
                overflow-x-auto overflow-y-hidden
                border-b border-slate-200/70
                dark:border-slate-700/70
                scrollbar-thin
            "
        >
            <button
                type="button"
                data-drop-folder-id="root"
                onClick={handleBreadcrumbRootClick}
                className={
                    "shrink-0 flex-none rounded-full border border-slate-200 px-3 py-1 text-xs transition-colors dark:border-slate-700 " +
                    (activeBreadcrumbTarget === "root"
                        ? "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                        : "bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200")
                }
            >
                Root
            </button>

            {breadcrumbs.map((crumb) => (
                <React.Fragment key={crumb.id}>
                    <span className="shrink-0 flex-none text-slate-300 dark:text-slate-600">
                        /
                    </span>

                    <button
                        type="button"
                        data-drop-folder-id={crumb.id}
                        onClick={() => handleBreadcrumbClick(crumb)}
                        className={
                            "shrink-0 flex-none rounded-full border border-slate-200 px-3 py-1 text-xs transition-colors dark:border-slate-700 " +
                            (activeBreadcrumbTarget === crumb.id
                                ? "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                : "bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200")
                        }
                    >
                        {crumb.name}
                    </button>
                </React.Fragment>
            ))}
        </div>
    );

    const renderSiteCard = (site, dragProvided = null, snapshot = null) => {
        const isActive = selectedSite && selectedSite.id === site.id;

        return (
            <div
                ref={dragProvided?.innerRef}
                {...(dragProvided ? dragProvided.draggableProps : {})}
                {...(dragProvided ? dragProvided.dragHandleProps : {})}
            >
                <SiteCard
                    site={site}
                    isActive={!!isActive}
                    isDragging={!!snapshot?.isDragging}
                    isCombineTarget={!!snapshot?.combineTargetFor}
                    onClick={() => handleRowClick(site)}
                />
            </div>
        );
    };

    const emptyState = (
        <div className="p-4 text-center text-xs text-slate-400 dark:text-slate-500">
            {currentFolderId ? "This folder is empty." : "There are no sites in the database."}
        </div>
    );

    const dndList = (
        <Droppable droppableId="sites-list" isCombineEnabled>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={LIST_WRAPPER_CLASS}
                >
                    {visibleSites.map((site, index) => (
                        <Draggable
                            key={site.id}
                            draggableId={String(site.id)}
                            index={index}
                            disableInteractiveElementBlocking
                        >
                            {(dragProvided, snapshot) =>
                                renderSiteCard(site, dragProvided, snapshot)
                            }
                        </Draggable>
                    ))}

                    {provided.placeholder}
                    {visibleSites.length === 0 && emptyState}
                </div>
            )}
        </Droppable>
    );

    const simpleList = (
        <div className={LIST_WRAPPER_CLASS}>
            {visibleSites.map((site) => {
                const isActive = selectedSite && selectedSite.id === site.id;

                return (
                    <SiteCard
                        key={site.id}
                        site={site}
                        isActive={!!isActive}
                        isDragging={false}
                        isCombineTarget={false}
                        onClick={() => handleRowClick(site)}
                    />
                );
            })}
            {visibleSites.length === 0 && emptyState}
        </div>
    );

    const canUseDnd =
        isAdmin &&
        scope === "all" &&
        (onReorder || onMoveToFolder || onMoveToRoot);

    return (
        <div>
            <div
                className="
                    rounded-2xl
                    border border-slate-200
                    bg-white/60
                    backdrop-blur-xl
                    dark:border-slate-700
                    dark:bg-slate-900/60
                    shadow-soft
                    h-[calc(100vh-11rem)]
                    flex flex-col
                    overflow-hidden
                    pb-2
                "
            >
                {header}

                <DragDropContext
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    {breadcrumbBar}

                    <div className="flex-1 overflow-y-auto">
                        {canUseDnd ? dndList : simpleList}
                    </div>
                </DragDropContext>
            </div>
        </div>
    );
}